/**
 * App-root error boundary. Without one, a render-time throw anywhere in
 * the tree unmounts the whole React subtree and leaves the user staring
 * at a blank page. This catches it and degrades to an honest error card,
 * logging the error + component stack to the console for diagnosis.
 *
 * Class component because React only exposes error-boundary semantics
 * (getDerivedStateFromError / componentDidCatch) to classes — there is
 * no hook equivalent. Deliberately minimal: it does not attempt recovery
 * or retry, just keeps a thrown error from blanking the app.
 */
import { Component } from "react";

export default class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { error: null };
    }

    static getDerivedStateFromError(error) {
        return { error };
    }

    componentDidCatch(error, info) {
        // Log for diagnosis — the boundary swallows the throw, so without
        // this the error would vanish silently.
        console.error("[ErrorBoundary] uncaught render error", error, info?.componentStack);
    }

    render() {
        if (this.state.error) {
            return (
                this.props.fallback ?? (
                    <div
                        role="alert"
                        style={{
                            display: "grid",
                            placeItems: "center",
                            minHeight: "100vh",
                            padding: "2rem",
                            background: "var(--bg, #0b0b0c)",
                            color: "var(--ink, #e8e8ea)",
                            fontFamily: "system-ui, sans-serif",
                        }}
                    >
                        <div style={{ maxWidth: 420, display: "grid", gap: "0.75rem", textAlign: "center" }}>
                            <h1 style={{ fontSize: "1.25rem", fontWeight: 500, margin: 0 }}>
                                Something went wrong
                            </h1>
                            <p style={{ fontSize: "0.875rem", lineHeight: 1.45, margin: 0, color: "var(--muted, #9a9aa0)" }}>
                                The dashboard hit an unexpected error and couldn't render this view.
                                Reloading the page usually clears it.
                            </p>
                        </div>
                    </div>
                )
            );
        }
        return this.props.children;
    }
}
